/*
*** CUSTOMIZATIONS
- Aggiunto bottone per esportare i bandi in CSV
- refresh dei filtri al cambio dati nello schema
*/

import React, { useState, useReducer, useEffect, createRef } from 'react';
import { getSearchBandiFilters } from 'design-comuni-plone-theme/actions';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Button,
  Spinner,
} from 'design-react-kit/dist/design-react-kit';
import moment from 'moment';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { getQueryStringResults } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { resetQuerystringResults } from 'design-comuni-plone-theme/actions';
import { Pagination } from 'design-comuni-plone-theme/components/ItaliaTheme';
import BandiInEvidenceTemplate from 'design-comuni-plone-theme/components/ItaliaTheme/Blocks/Listing/BandiInEvidenceTemplate';
import FiltersConfig from 'design-comuni-plone-theme/components/ItaliaTheme/Blocks/BandiSearch/FiltersConfig';

/* import export csv */
import { Toast, Icon } from '@plone/volto/components';
import downloadSVG from '@plone/volto/icons/download.svg';
import { bandiCsvExport } from '@package/actions';

const messages = defineMessages({
  find: {
    id: 'find',
    defaultMessage: 'Cerca',
  },
  noResult: {
    id: 'noResult',
    defaultMessage: 'Nessun risultato trovato',
  },
  exportCsv: {
    id: 'exportCsv',
    defaultMessage: 'Esporta in OpenFormat',
  },
  export_error_msg: {
    id: 'exportErrorLabel',
    defaultMessage: "Si è verificato un problema durante l'esportazione:",
  },
  export_error_generic_msg: {
    id: 'exportErrorGenericLabel',
    defaultMessage: "per favore contatta l'amministratore del sito.",
  },
});

const Body = ({ data, id, inEditMode, path, onChangeBlock, toastify }) => {
  const intl = useIntl();
  const b_size = 6;

  moment.locale(intl.locale);

  const [currentPage, setCurrentPage] = useState(1);
  const subsite = useSelector((state) => state.subsite?.data);
  const exportState = useSelector((state) => state.bandiCsv);

  const dispatch = useDispatch();

  const querystringResults = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[id + '_bandi_search'];
  });

  const items = useSelector((state) => {
    return (
      state.querystringsearch?.subrequests?.[id + '_bandi_search']?.items ?? []
    );
  });

  const loading = useSelector((state) => {
    return (
      state.querystringsearch?.subrequests?.[id + '_bandi_search']?.loading ||
      false
    );
  });

  const resultsRef = createRef();

  const doRequest = (page = currentPage, csvExport = false) => {
    const query = [
      {
        i: 'portal_type',
        o: 'plone.app.querystring.operation.selection.any',
        v: ['Bando'],
      },
    ];

    [filterOne, filterTwo, filterThree].forEach((f) => {
      if (f?.widget) {
        const value = f.widget.props.value;
        if (f.query) {
          f.query(value, query);
        }
      }
    });

    if (data.location && data.location[0]) {
      query.push({
        i: 'path',
        o: 'plone.app.querystring.operation.string.absolutePath',
        v: flattenToAppURL(data.location[0]['@id']),
      });
    }

    if (data.defaultQuerystring) {
      query.push(
        ...data.defaultQuerystring.filter(
          (el) => !query.map((q) => q.i).includes(el.i)
        ),
      );
    }

    if (!csvExport) {
      dispatch(
        getQueryStringResults(
          subsite ? flattenToAppURL(subsite['@id']) : '',
          {
            fullobjects: 1,
            query: query,
            b_size: b_size,
            sort_on: data.sort_on,
            sort_order: data.sort_order ? 'descending' : 'ascending',
          },
          id + '_bandi_search',
          page,
        ),
      );
    } else {
      dispatch(
        bandiCsvExport({
          query: query,
          sort_on: data.sort_on,
          sort_order: data.sort_order ? 'descending' : 'ascending',
        }),
      );
    }
  };

  // Se cambia uno dei tre filtri o una impostazione nella sidebar, resetto lo stato dei filtri
  useEffect(() => {
    if (data.display_results_by_default) {
      doRequest(1);
    } else {
      dispatchFilter({ type: 'reset' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const filtersReducer = (state = getInitialState(), action) => {
    let newState = {
      ...state,
    };

    if (action.type === 'reset') {
      newState = {
        ...getInitialState(),
      };
      dispatch(resetQuerystringResults(id + '_bandi_search'));
    } else {
      const f = newState[action.filter];
      const defaultReducer = (value, state) => value;
      const reducer = f.reducer || defaultReducer;
      f.widget.props.value = reducer(action.value, state[action.filter]);
    }
    return newState;
  };

  const pathSearch = data?.location?.length > 0 ? data.location[0]['@id'] : '/';
  const filtersConfig = FiltersConfig(null, pathSearch, data);

  const getInitialState = () => {
    dispatch(getSearchBandiFilters(pathSearch || '/'));
    return {
      filterOne: filtersConfig[data?.filter_one],
      filterTwo: filtersConfig[data?.filter_two],
      filterThree: filtersConfig[data?.filter_three],
    };
  };

  const [{ filterOne, filterTwo, filterThree }, dispatchFilter] = useReducer(
    filtersReducer,
    getInitialState(),
  );

  function handleQueryPaginationChange(e, { activePage }) {
    resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    const current = activePage?.children ?? 1;
    setCurrentPage(current);
    doRequest(current);
  }

  // Export in CSV
  useEffect(() => {
    if (exportState.error) {
      const msg =
        exportState.error?.response?.body?.message ||
        intl.formatMessage(messages.export_error_generic_msg);
      const errorMsg = `${intl.formatMessage(
        messages.export_error_msg,
      )} ${msg}`;
      toastify.toast.error(<Toast error content={errorMsg} />);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportState]);

  let Variation = BandiInEvidenceTemplate;
  if (data.variation?.length > 0) {
    let filtered_variations =
      config.blocks.blocksConfig.searchBandi.results_variations.filter(
        (v) => v.id === data.variation,
      );
    if (filtered_variations.length > 0) {
      Variation = filtered_variations[0].component;
    }
  }

  return filterOne || filterTwo || filterThree ? (
    <Container>
      <div
        className={cx('rounded bg-' + (data.bg_color || 'primary'), {
          'public-ui': inEditMode,
        })}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            doRequest(1);
          }}
        >
          <div className="d-flex justify-content-center">
            <div className="d-flex search-container align-items-center justify-content-center flex-wrap">
              {filterOne && (
                <>
                  {React.createElement(filterOne.widget.component, {
                    ...filterOne.widget?.props,
                    id: 'filterOne',
                    onChange: (filter, value) => {
                      dispatchFilter({
                        filter: filter,
                        value: value,
                      });
                    },
                  })}
                </>
              )}
              {filterTwo &&
                React.createElement(filterTwo.widget?.component, {
                  ...filterTwo.widget?.props,
                  id: 'filterTwo',
                  onChange: (filter, value) =>
                    dispatchFilter({
                      filter: filter,
                      value: value,
                    }),
                })}
              {filterThree &&
                React.createElement(filterThree.widget?.component, {
                  ...filterThree.widget?.props,
                  id: 'filterThree',
                  onChange: (filter, value) =>
                    dispatchFilter({
                      filter: filter,
                      value: value,
                    }),
                })}

              <Button
                color={data.button_color || 'tertiary'}
                icon={false}
                tag="button"
                className="my-2 my-lg-1"
              >
                {intl.formatMessage(messages.find)}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {!loading ? (
        items?.length > 0 ? (
          <div className="mt-4" ref={resultsRef} aria-live="polite">
            <div className="block listing">
              <Variation items={items} full_width={false} data={data} />
            </div>
            {querystringResults.total > b_size && (
              <Pagination
                activePage={currentPage}
                totalPages={Math.ceil(querystringResults.total / b_size)}
                onPageChange={handleQueryPaginationChange}
              />
            )}
            {/* CSV Button */}
            <div className="csv-export-button mb-3">
              <Button
                onClick={() => doRequest(currentPage, true)}
                size="tiny"
                className="ml-3"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon name={downloadSVG} size="1.5rem" className="pr-2" />
                {intl.formatMessage(messages.exportCsv)}
              </Button>
            </div>
          </div>
        ) : querystringResults ? (
          <>
            <div className="mt-4" aria-live="polite">
              <p className="text-center">
                {intl.formatMessage(messages.noResult)}
              </p>
            </div>
          </>
        ) : null
      ) : (
        <div className="d-flex justify-content-center mt-3">
          <Spinner active />
        </div>
      )}
    </Container>
  ) : null;
};
export default Body;
